/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useEffect, useState } from "react";

//@Packages
import {
  Button,
  Card,
  CardContent,
  CloseIcon,
  ContentLoader,
  Input,
  SearchIcon,
  Table,
  TableContent,
  TableHeader,
  TrashIcon
} from "@forge/common";
import { useTranslation } from "react-i18next";

//@Models
import { ISiteListProps } from "../../../models";
import { GatewaySiteProps } from "../../../models/devicelist";

//@Static Data
import { bulkAssignmentSiteTableHeaders } from "../../../utils/staticdata";

import BulkAssignmentAssignSites from "../BulkAssignmentAssignSites";
import { LOCALE_CONST } from "../../../utils/constants";

interface DownstreamDeviceListProps {
  title?: string;
  deviceId?: string;
  isDisabled?: boolean;
  data?: ISiteListProps[];
  selectedData?: GatewaySiteProps[];
  setSiteValueSelection?: (lists: GatewaySiteProps[]) => void;
  siteLoading?: boolean;
  siteFetching?: boolean;
  onSiteSelect?: (site: ISiteListProps[]) => void;
  orgMetaFetching?: boolean;
  orgMetaLoading?: boolean;
}

//@Functional component
const OverviewTable: React.FC<DownstreamDeviceListProps> = ({
  isDisabled,
  data,
  title,
  siteLoading,
  siteFetching,
  onSiteSelect,
  orgMetaFetching,
  orgMetaLoading
}) => {
  const { t } = useTranslation();

  const [showAssignSite, setShowAssignSite] = useState<boolean>(false);
  const [deviceLists, setDeviceLists] = useState<ISiteListProps[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedData, setSelectedData] = useState<GatewaySiteProps[]>([]);

  useEffect(() => {    
    setDeviceLists([]);
    setSelectedData([]);
  }, [data]);

  const formatContentData = (content: ISiteListProps[]) => {
    const formatCellContent = (value: any) => value || "-";

    return content?.map((item: ISiteListProps, index: number) => {
      return {
        id: (index + 1).toString(),
        cell: bulkAssignmentSiteTableHeaders.map((header) => {
          let cellContent: any = (
            <span>{formatCellContent(item[header.id])}</span>
          );

          if (header.id === "action") {
            cellContent = (
              <div className="gateway-action-button-container">
                <Button
                  aria-label="Settings"
                  data-testid={`test-delete-device-${index}`}
                  icon={
                    <TrashIcon
                      strokeWidth={2.5}
                      color="var(--link-name-color)"
                    />
                  }
                  variant="quiet-secondary"
                  onClick={() => {
                    const tempData = selectedData.filter(
                      (prevItem) => prevItem.id !== item.id
                    );
                    setSearchValue("");
                    setSelectedData(tempData);                    
                  }}
                />
              </div>
            );
          }
          return { content: cellContent };
        }),
      };
    });
  };

  const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, " ").trim();
  const searchOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    const filteredData = selectedData.filter((item) => {
      return normalizeSpaces(item.name).includes(
        normalizeSpaces(event.target.value)
      );
    });
    setDeviceLists(filteredData);
  };

  const onSearchClear = () => {
    setDeviceLists(selectedData);
    setSearchValue("");
  };

  const handleConfirm = (lists: GatewaySiteProps[]) => {
    setSelectedData(lists);     
    setDeviceLists(lists);
  };

  useEffect(() => {
    onSiteSelect(selectedData);
    setDeviceLists(selectedData);
  }, [selectedData]);

  return (
    <>
      <div className="bulk-assignment-site-table-wrap">
        <Card className="overviewtable-skip">
          <div className="flex justify-end w-full mb-2 customize-table-wrap">
            {title && <div className="table-custom-title-cls">{title}</div>}
            {selectedData && selectedData.length > 0 && (
              <>
                <div className="w-1/6 mr-4">
                  <Input
                    type="text"
                    data-testid="bulk-assignment-search-test"
                    placeholder={t(LOCALE_CONST.searchByName)}
                    id="search"
                    onChange={searchOnChange}
                    maxLength={50}
                    value={searchValue}
                    prefix={<SearchIcon strokeWidth={2} />}
                    suffix={
                      searchValue ? (
                        <div
                          className="cursor-pointer"
                          data-testid="search-close-test"
                          onClick={onSearchClear}
                        >
                          <CloseIcon data-testid="search-close" />
                        </div>
                      ) : (
                        <></>
                      )
                    }
                    size="regular"
                  />
                </div>
                <div className="w-1/8">
                  <Button
                    variant="primary"
                    onClick={() => setShowAssignSite(true)}
                    disabled={isDisabled}
                  >
                    {t(LOCALE_CONST.associateSite)}
                  </Button>
                </div>
              </>
            )}            
          </div>
          {siteLoading || siteFetching || orgMetaFetching || orgMetaLoading ? (
            <ContentLoader> {t(LOCALE_CONST.loadingDetails)}...</ContentLoader>
          ) : (
            <>
              {deviceLists && deviceLists.length > 0 ? (
                <>
                  <CardContent x="none" top="none">
                    <Table onCard>
                      <TableHeader data={bulkAssignmentSiteTableHeaders} />
                      <TableContent data={formatContentData(deviceLists)} />
                    </Table>
                  </CardContent>
                  <CardContent></CardContent>
                </>
              ) : (
                <CardContent className="place-items-center grid">
                  {searchValue !== "" ? (
                    <p>{t(LOCALE_CONST.noResultsFound)}</p>
                  ) : (
                    <>
                      <p className="text-3xl">{!isDisabled ? t(LOCALE_CONST.noSitesSelected) : t(LOCALE_CONST.orgNotFound)}</p>
                      <p className="pt-1 pb-5">
                        {!isDisabled ? t(LOCALE_CONST.associateSiteOnClick) : t(LOCALE_CONST.orgOnboardingStatus)}
                      </p>
                      {!isDisabled && 
                        <Button
                          variant="primary"
                          onClick={() => setShowAssignSite(true)}
                          disabled={isDisabled}
                          size="large"
                        >
                          {t(LOCALE_CONST.associateSite)}
                        </Button>
                      }
                    </>
                  )}
                </CardContent>
              )}
            </>
          )}
        </Card>
        <BulkAssignmentAssignSites
          showAssignSite={showAssignSite}
          onClose={() => setShowAssignSite(false)}
          onConfirm={handleConfirm}
          sitesData={data || []}
          selectedData={deviceLists || []}
        />
      </div>
    </>
  );
};

export default OverviewTable;
